import { useIsFocused } from '@react-navigation/native';
import React from 'react';
import { createContext } from 'react';

export type LayoutChangeEventProviderContextValue = {
    registerLayoutChangeListener: (listener: () => void) => void;
    unRegisterLayoutChangeListener: (listener: () => void) => void;
    onLayoutChange: () => void;
};

export const LayoutChangeEventProviderContext = createContext<LayoutChangeEventProviderContextValue>({
    registerLayoutChangeListener: () => {},
    unRegisterLayoutChangeListener: () => {},
    onLayoutChange: () => {},
});

const LayoutChangeEventProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const registeredListeners = React.useRef<(() => void)[]>([]);
    const onLayoutChange = React.useCallback(() => {
        registeredListeners.current.forEach((listener) => listener());
    }, []);
    const registerLayoutChangeListener = React.useCallback((listener: () => void) => {
        const withoutListener = registeredListeners.current.filter((_) => _ !== listener);
        registeredListeners.current = [...withoutListener, listener];
    }, []);
    const unRegisterLayoutChangeListener = React.useCallback((listener: () => void) => {
        registeredListeners.current = registeredListeners.current.filter((_) => _ !== listener);
    }, []);

    const context: LayoutChangeEventProviderContextValue = React.useMemo(
        () => ({
            registerLayoutChangeListener,
            unRegisterLayoutChangeListener,
            onLayoutChange,
        }),
        [registerLayoutChangeListener, unRegisterLayoutChangeListener, onLayoutChange],
    );

    return (
        <LayoutChangeEventProviderContext.Provider value={context}>
            {children}
        </LayoutChangeEventProviderContext.Provider>
    );
};

export default React.memo(LayoutChangeEventProvider);

export const useOnLayoutChange = () => {
    const { onLayoutChange } = React.useContext(LayoutChangeEventProviderContext);
    return onLayoutChange;
};

/**
 * Will call the listener when the layout changes.  This can be caused by a change in a scroll or a change in the size of the window.
 *
 * The listener should be memoized to prevent unnecessary re-renders and effecient performance.
 */
export const useRegisterOnLayoutChangeListener = (listener: () => void) => {
    const { registerLayoutChangeListener, unRegisterLayoutChangeListener } = React.useContext(
        LayoutChangeEventProviderContext,
    );
    const isFocused = useIsFocused();

    React.useEffect(() => {
        return () => {
            unRegisterLayoutChangeListener(listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (isFocused) {
            registerLayoutChangeListener(listener);
        } else {
            unRegisterLayoutChangeListener(listener);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);
};
